import React from 'react';

/**
 * ELEGANT editorial botanical leaf — smooth Bezier curves, refined proportions.
 * Uses fine line-art with subtle vein detail. Not childlike.
 */
export const BotanicalLeaf = ({ className = '', size = 140, opacity = 0.4, flip = false, rotate = 0 }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 100 220"
    width={size * 0.55}
    height={size * 1.2}
    className={`pointer-events-none select-none ${className}`}
    style={{ opacity, transform: `rotate(${rotate}deg) ${flip ? 'scaleX(-1)' : ''}` }}
  >
    <g fill="none" stroke="#6B5A3E" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M50 210 C 22 180, 12 120, 24 60 C 32 25, 44 10, 52 8 C 60 10, 72 25, 80 60 C 92 120, 82 180, 50 210 Z" strokeWidth="1.0" />
      <path d="M50 200 C 49 160, 48 110, 50 20" strokeWidth="0.6" opacity="0.7" />
      <path d="M46 180 C 38 172, 32 168, 28 160" strokeWidth="0.5" opacity="0.6" />
      <path d="M46 150 C 36 142, 28 138, 22 128" strokeWidth="0.5" opacity="0.6" />
      <path d="M46 120 C 34 112, 26 108, 20 98" strokeWidth="0.5" opacity="0.6" />
      <path d="M47 90 C 36 82, 30 78, 24 70" strokeWidth="0.5" opacity="0.6" />
      <path d="M49 60 C 40 54, 34 50, 30 44" strokeWidth="0.5" opacity="0.6" />
      <path d="M54 180 C 62 172, 68 168, 72 160" strokeWidth="0.5" opacity="0.6" />
      <path d="M54 150 C 64 142, 72 138, 78 128" strokeWidth="0.5" opacity="0.6" />
      <path d="M54 120 C 66 112, 74 108, 80 98" strokeWidth="0.5" opacity="0.6" />
      <path d="M53 90 C 64 82, 70 78, 76 70" strokeWidth="0.5" opacity="0.6" />
      <path d="M51 60 C 60 54, 66 50, 70 44" strokeWidth="0.5" opacity="0.6" />
    </g>
  </svg>
);

/**
 * ELEGANT classical typographic Fleuron — symmetric scroll flourish.
 * Perfect editorial divider between sections.
 */
export const Fleuron = ({ className = '', size = 80, opacity = 0.5 }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 240 60"
    width={size * 3}
    height={size * 0.75}
    className={`pointer-events-none select-none ${className}`}
    style={{ opacity }}
  >
    <g fill="none" stroke="#6B5A3E" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 30 L 80 30" strokeWidth="0.6" opacity="0.5" />
      <path d="M85 30 C 92 30, 98 22, 108 22 C 118 22, 118 32, 108 32 C 100 32, 96 26, 100 22" />
      <path d="M100 22 C 96 18, 92 18, 92 22" strokeWidth="0.7" opacity="0.7" />
      <circle cx="120" cy="30" r="2.2" fill="#6B5A3E" stroke="none" opacity="0.85" />
      <circle cx="120" cy="30" r="5" strokeWidth="0.7" />
      <path d="M155 30 C 148 30, 142 22, 132 22 C 122 22, 122 32, 132 32 C 140 32, 144 26, 140 22" />
      <path d="M140 22 C 144 18, 148 18, 148 22" strokeWidth="0.7" opacity="0.7" />
      <path d="M160 30 L 230 30" strokeWidth="0.6" opacity="0.5" />
    </g>
  </svg>
);

/**
 * Delicate curled sprig — refined ornament for corners.
 */
export const CurledSprig = ({ className = '', size = 90, opacity = 0.4, rotate = 0, flip = false }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 100 100"
    width={size}
    height={size}
    className={`pointer-events-none select-none ${className}`}
    style={{ opacity, transform: `rotate(${rotate}deg) ${flip ? 'scaleX(-1)' : ''}` }}
  >
    <g fill="none" stroke="#6B5A3E" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 88 C 30 78, 42 64, 50 48 C 58 32, 68 22, 82 18" strokeWidth="1.0" />
      <path d="M82 18 C 88 18, 90 22, 86 26 C 82 30, 76 26, 82 18" strokeWidth="0.8" />
      <path d="M42 62 C 34 58, 30 62, 32 68 C 38 68, 44 66, 42 62 Z" strokeWidth="0.8" />
      <path d="M42 62 C 42 55, 38 52, 36 54" strokeWidth="0.6" opacity="0.7" />
      <path d="M58 42 C 52 38, 48 42, 50 48 C 56 48, 60 46, 58 42 Z" strokeWidth="0.8" />
      <path d="M58 42 C 58 36, 54 33, 52 35" strokeWidth="0.6" opacity="0.7" />
      <path d="M74 26 C 68 22, 64 26, 66 32 C 72 32, 76 30, 74 26 Z" strokeWidth="0.8" />
    </g>
  </svg>
);

/**
 * Corner arabesque — elegant editorial corner ornament.
 */
export const CornerArabesque = ({ className = '', size = 120, opacity = 0.4, corner = 'tl' }) => {
  const transform = {
    tl: 'rotate(0deg)',
    tr: 'rotate(90deg)',
    br: 'rotate(180deg)',
    bl: 'rotate(270deg)',
  }[corner];
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={`pointer-events-none select-none ${className}`}
      style={{ opacity, transform }}
    >
      <g fill="none" stroke="#6B5A3E" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 50 C 8 28, 28 8, 50 8" />
        <path d="M12 50 C 12 30, 30 12, 50 12" strokeWidth="0.6" opacity="0.5" />
        <path d="M28 22 C 24 26, 24 32, 30 34 C 34 32, 34 26, 30 22 C 26 20, 22 24, 28 22 Z" strokeWidth="0.8" />
        <path d="M50 8 C 50 4, 54 4, 54 8" strokeWidth="0.7" />
        <circle cx="52" cy="10" r="1.4" fill="#6B5A3E" stroke="none" opacity="0.85" />
        <path d="M8 50 C 4 50, 4 54, 8 54" strokeWidth="0.7" />
        <circle cx="10" cy="52" r="1.4" fill="#6B5A3E" stroke="none" opacity="0.85" />
      </g>
    </svg>
  );
};

/** Backward-compat aliases — now render the new elegant designs */
export const Praprot = (props) => <BotanicalLeaf {...props} />;
export const Vejica2 = (props) => <CurledSprig {...props} />;
export const Cvet = ({ className = '', size = 60, opacity = 0.4 }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 60 60"
    width={size}
    height={size}
    className={`pointer-events-none select-none ${className}`}
    style={{ opacity }}
  >
    <g fill="none" stroke="#6B5A3E" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round">
      {[0, 60, 120, 180, 240, 300].map((a, i) => (
        <g key={i} transform={`rotate(${a} 30 30)`}>
          <path d="M30 14 C 26 18, 25 24, 28 28 C 31 28, 33 26, 30 14 Z" strokeWidth="0.8" />
        </g>
      ))}
      <circle cx="30" cy="30" r="2.6" strokeWidth="0.7" />
      <circle cx="30" cy="30" r="0.9" fill="#6B5A3E" stroke="none" opacity="0.85" />
    </g>
  </svg>
);

/**
 * Soft floating blob for section corners. Purely decorative, aria-hidden.
 */
export const BlobDecor = ({
  color = 'rgba(233, 224, 205, 0.6)',
  size = 320,
  className = '',
  style,
  variant = 'slow',
}) => (
  <span
    aria-hidden="true"
    className={`pointer-events-none absolute rounded-full blur-3xl ${variant === 'slower' ? 'animate-float-slower' : 'animate-float-slow'} ${className}`}
    style={{
      width: size,
      height: size,
      background: color,
      ...style,
    }}
  />
);

/**
 * Layered organic wave silhouettes — decorative background.
 */
export const WaveSilhouettes = ({ className = '' }) => (
  <div
    aria-hidden="true"
    className={`pointer-events-none absolute inset-x-0 bottom-0 overflow-hidden ${className}`}
    style={{ height: '35%' }}
  >
    <svg viewBox="0 0 1440 300" preserveAspectRatio="none" className="absolute inset-x-0 bottom-0 w-full h-full">
      <path d="M0,220 C240,180 480,240 720,200 C960,160 1200,230 1440,190" fill="none" stroke="rgba(140, 130, 108, 0.18)" strokeWidth="1.2" />
      <path d="M0,250 C220,220 420,265 720,235 C1020,205 1200,255 1440,225" fill="none" stroke="rgba(140, 130, 108, 0.14)" strokeWidth="1.2" />
      <path d="M0,278 C280,258 480,286 720,268 C960,250 1200,280 1440,262" fill="none" stroke="rgba(140, 130, 108, 0.1)" strokeWidth="1" />
    </svg>
  </div>
);

/**
 * WaveDivider — simple sinuous line separator.
 */
export const WaveDivider = ({ className = '' }) => (
  <svg aria-hidden="true" viewBox="0 0 200 20" className={`inline-block ${className}`} width="200" height="20">
    <path d="M0 10 Q 25 2, 50 10 T 100 10 T 150 10 T 200 10" fill="none" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
  </svg>
);

/**
 * OrnamentSprig — kept for backward compat.
 */
export const OrnamentSprig = ({ className = '', size = 44 }) => (
  <svg aria-hidden="true" viewBox="0 0 44 12" width={size} height={size * 0.3} className={className}>
    <g fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round">
      <path d="M2 6q6-4 20-4t20 4" />
      <path d="M12 6q-1-2-3-2M32 6q1-2 3-2" />
    </g>
  </svg>
);
